import { isValidCoordinate } from "geolib";

import {
  getRideDtoStations,
  ILiveMapIncidentItem,
  IRideRoute,
  IRideTableDto,
  normalizeAndSortLocations,
} from "@ryde/shared";

import {
  buildDefaultIncidentMarkerHtml,
  buildTypedIncidentMarkerHtml,
  resolveLiveMapIncidentMarkerPresentation,
} from "./live-map.incident-marker.mapping";
import type { ILiveMapIncidentGeoPosition } from "./live-map.incidents-layer.models";

export const pickFirstStationLatLng = (
  ride: IRideTableDto,
): ILiveMapIncidentGeoPosition | undefined => {
  const stations = getRideDtoStations(ride);

  for (const s of stations) {
    const lat = s.locationInfo?.latitude;
    const lng = s.locationInfo?.longitude;

    if (
      typeof lat === "number" &&
      typeof lng === "number" &&
      isValidCoordinate({ lat, lng })
    ) {
      const position: ILiveMapIncidentGeoPosition = {
        latitude: lat,
        longitude: lng,
      };
      return position;
    }
  }

  return undefined;
};

export const lastTrackingLatLng = (
  route: IRideRoute | undefined,
): ILiveMapIncidentGeoPosition | undefined => {
  if (!route?.locations?.length) {
    return undefined;
  }

  const sorted = normalizeAndSortLocations(route.locations);
  const last = sorted.at(-1);

  if (!last) {
    return undefined;
  }

  const { latitude, longitude } = last;

  if (
    typeof latitude !== "number" ||
    typeof longitude !== "number" ||
    !isValidCoordinate({ lat: latitude, lng: longitude })
  ) {
    return undefined;
  }

  const position: ILiveMapIncidentGeoPosition = { latitude, longitude };
  return position;
};

export const resolveIncidentMapPosition = (
  incident: ILiveMapIncidentItem,
  trackingByRideId: ReadonlyMap<number, IRideRoute>,
  rideById: ReadonlyMap<number, IRideTableDto>,
): ILiveMapIncidentGeoPosition | undefined => {
  const latRaw = incident.latitude;
  const lngRaw = incident.longitude;

  if (
    typeof latRaw === "number" &&
    typeof lngRaw === "number" &&
    isValidCoordinate({ lat: latRaw, lng: lngRaw })
  ) {
    const position: ILiveMapIncidentGeoPosition = {
      latitude: latRaw,
      longitude: lngRaw,
    };
    return position;
  }

  const rideId = incident.rideId ?? undefined;

  if (rideId) {
    const fromTracking = lastTrackingLatLng(trackingByRideId.get(rideId));

    if (fromTracking) {
      return fromTracking;
    }

    const ride = rideById.get(rideId);

    if (ride) {
      return pickFirstStationLatLng(ride);
    }
  }

  return undefined;
};

export interface IBuildIncidentDivIconParams {
  hasOpenedPopupOnMap: boolean;
  incidentTypeIconType?: string | null;
  incidentTypeColor?: string | null;
}

export const buildIncidentMarkerHtml = ({
  hasOpenedPopupOnMap,
  incidentTypeIconType,
  incidentTypeColor,
}: IBuildIncidentDivIconParams): string => {
  const presentation = resolveLiveMapIncidentMarkerPresentation({
    hasOpenedPopupOnMap,
    incidentTypeIconType,
    incidentTypeColor,
  });

  return presentation.variant === "typed" && presentation.iconType
    ? buildTypedIncidentMarkerHtml({
        ...presentation,
        iconType: presentation.iconType,
      })
    : buildDefaultIncidentMarkerHtml({
        color: presentation.color,
        hasOpenedPopupOnMap: presentation.hasOpenedPopupOnMap,
      });
};
